import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Icon, Modal } from "semantic-ui-react";
import { AbuseReportForm } from "./AbuseReportForm";
import axios from "axios";
import isEmail from "validator/lib/isEmail";

// Axios config with CSRF and Invenio headers
const axiosWithConfig = axios.create({
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/vnd.inveniordm.v1+json",
  },
});

// API call
export const reportAbuse = async (pid, data) => {
  return axiosWithConfig.post(`/api/records/${pid}/report-abuse`, data);
};

export const AbuseReportModal = ({ pid }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    abuseType: "",
    message: "",
    name: "",
    affiliation: "",
    email: "",
    address: "",
    city: "",
    country: "",
    zip: "",
    phone: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetForm = () => {
    setFormData({
      abuseType: "",
      message: "",
      name: "",
      affiliation: "",
      email: "",
      address: "",
      city: "",
      country: "",
      zip: "",
      phone: "",
    });
    setFieldErrors({});
    setError("");
    setSuccess("");
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.abuseType) errors.abuseType = "Abuse type is required.";
    if (formData.message.trim().length < 10)
      errors.message = "Message must be at least 10 characters.";
    if (!formData.name) errors.name = "Name is required.";
    if (!formData.affiliation) errors.affiliation = "Affiliation is required.";
    if (!formData.email || !isEmail(formData.email))
      errors.email = "A valid email is required.";
    if (!formData.address) errors.address = "Address is required.";
    if (!formData.city) errors.city = "City is required.";
    if (!formData.zip) errors.zip = "Zip code is required.";
    if (!formData.phone) errors.phone = "Phone number is required.";
    if (!formData.country) errors.country = "Country is required.";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    const isValid = validateForm();
    if (!isValid) {
      setError("Please fix the highlighted errors.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const payload = { ...formData, message: formData.message.trim() };

    reportAbuse(pid, payload)
      .then(() => {
        setSuccess("Thank you. Your report has been submitted.");
        setTimeout(() => {
          setOpen(false);
          resetForm();
        }, 1500);
      })
      .catch((error) => {
        console.error("Abuse report error:", error);
        setError("An error occurred while submitting your report.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleModalOpen = () => {
    resetForm();
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        warning
        icon
        labelPosition="left"
        size="medium"
        fluid
        onClick={handleModalOpen}
      >
        <Icon name="flag" />
        Report abuse
      </Button>

      <Modal onClose={handleModalClose} open={open}>
        <Modal.Header>Report Abuse or Inappropriate Content</Modal.Header>
        <Modal.Content scrolling>
          <AbuseReportForm
            formData={formData}
            handleChange={handleChange}
            error={error}
            success={success}
            fieldErrors={fieldErrors}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleModalClose} disabled={loading}>
            Cancel
          </Button>
          <Button primary onClick={handleSubmit} disabled={loading} loading={loading}>
            Submit Report
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

AbuseReportModal.propTypes = {
  pid: PropTypes.string.isRequired,
};
